import { CoreTextView } from '@core/widgets';
import { numberFormat, useApp } from '@libs/core';
import { AssetsSvgMap, TAssetsSvgMapValue, TFeeOption } from '@libs/types';
import React from 'react';
import { TextStyle } from '../../../types/styles';
import { ButtonRowView } from '../../Buttons/ButtonRowView';
import { VisualView } from '../../Image/VisualView';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import PricingTab from '../pricing-tab/PricingTab';
import { FeeBlock } from './FeeBlock';

type TFeePanelProps = {
    sum?: string;
    currency?: string;
    iconName?: TAssetsSvgMapValue;
    label: string;
    text: string;
    feeBottomSheetFirstText?: string;
    feeBottomSheetSecondText?: string;
    feeOptions?: TFeeOption[];
    bottomSheetButton: string;
    parentStyleNames?: string[];
};

const Fee = React.memo(
    ({
        sum,
        iconName,
        label,
        text,
        feeBottomSheetFirstText,
        feeBottomSheetSecondText,
        feeOptions,
        bottomSheetButton,
        parentStyleNames,
        currency = '',
    }: TFeePanelProps) => {
        const { setPopup } = useApp();
        const handleClick = () => {
            setPopup(
                <PopupComponent
                    header={
                        <>
                            <CoreTextView
                                text={feeBottomSheetFirstText ?? ''}
                                styleName="calculatorBsText"
                                parentStyleNames={parentStyleNames}
                                styleProps={{ ...TextStyle.main, main: 'var(--black)', textAlign: 'center' as const }}
                            />
                            {feeOptions?.map((option, index, array) => {
                                const marginBottom = index === array.length - 1 ? 32 : 16;
                                const marginTop = index === 0 ? 32 : undefined;
                                return (
                                    <PricingTab
                                        key={index + option.optionName}
                                        text={option?.optionName}
                                        fee={`${option?.optionFee?.value ?? ''} ${option?.optionFee?.currency ?? ''} ${option?.optionFee?.label}`}
                                        marginTop={marginTop}
                                        marginBottom={marginBottom}
                                        parentStyleNames={parentStyleNames}
                                    />
                                );
                            })}
                            <CoreTextView
                                text={feeBottomSheetSecondText ?? ''}
                                styleName="calculatorBsText"
                                parentStyleNames={parentStyleNames}
                                styleProps={{ ...TextStyle.main, main: 'var(--black)', textAlign: 'center' as const }}
                            />
                        </>
                    }
                    externalContent={[
                        <ButtonRowView
                            key="button_ic_check"
                            label={bottomSheetButton}
                            iconName={'ic_check'}
                            parentStyleNames={parentStyleNames}
                            onClick={() => setPopup(null)}
                        />,
                    ]}
                />,
            );
        };
        return (
            <FeeBlock
                icon={
                    iconName ? (
                        <VisualView
                            as="button"
                            uid="calculator_info_icon_fee"
                            iconName={iconName}
                            defaultIconName={AssetsSvgMap.ic_x}
                            iconWidth={16}
                            iconHeight={16}
                            iconColor="var(--grey_72_lc)"
                            hovered={{ backgroundColor: 'var(--grey_16_lc)' }}
                            parentStyleNames={parentStyleNames}
                            styleName="calculatorInfoIcon"
                            handleClick={handleClick}
                            margin={{ left: 6 }}
                        />
                    ) : null
                }
                title={label ?? ''}
                text={'' + text}
                rate={`${numberFormat(sum)} ${currency ?? ''}`.trim()}
                parentStyleNames={parentStyleNames}
            />
        );
    },
);
export { Fee };
